import React, { useEffect, useState } from 'react';
import { Container, Cpu, HardDrive, Activity, RefreshCw, Square, Play, AlertTriangle } from 'lucide-react';
import { GetDockerStats } from './apis/get';
import { DockerContainerAction } from './apis/post';

interface ContainerStat {
  name: string;
  container_id: string;
  cpu_percent: string;
  memory_usage: string;
  memory_percent: string;
  net_io: string;
  block_io: string;
  pids: string;
  warning?: string;
  needs_restart?: boolean;
}

interface DockerStatsData {
  total_containers: number;
  containers: ContainerStat[];
  warning?: string;
  action_required?: string;
}

export default function DockerManagement() {
  const [stats, setStats] = useState<DockerStatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ show: boolean; action: string; container: string } | null>(null);
  const [cpuHistory, setCpuHistory] = useState<Record<string, number[]>>({});
  const [alerts, setAlerts] = useState<Record<string, boolean>>({});

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await GetDockerStats();
      if (response?.status && response?.data) {
        setStats(response.data);
        
        const newHistory = { ...cpuHistory };
        const newAlerts = { ...alerts };
        
        response.data.containers.forEach((container: ContainerStat) => {
          if (container.needs_restart) {
            newAlerts[container.name] = true;
          } else {
            const cpuValue = parseFloat(container.cpu_percent.replace('%', ''));
            if (!newHistory[container.name]) {
              newHistory[container.name] = [];
            }
            newHistory[container.name].push(cpuValue);
            if (newHistory[container.name].length > 5) {
              newHistory[container.name].shift();
            }
            
            if (newHistory[container.name].length === 5 && 
                newHistory[container.name].every(val => val === 0)) {
              newAlerts[container.name] = true;
            } else {
              newAlerts[container.name] = false;
            }
          }
        });
        
        setCpuHistory(newHistory);
        setAlerts(newAlerts);
      }
    } catch (error) {
      console.error('Failed to fetch Docker stats', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // const interval = setInterval(fetchStats, 30000);
    // return () => clearInterval(interval);
  }, []);

  const handleAction = async (action: string, containerName: string) => {
    setActionLoading(`${action}-${containerName}`);
    try {
      const response = await DockerContainerAction(action, containerName);
      if (response?.status) {
        alert(response.message);
        fetchStats();
      }
    } catch (error) {
      alert('Action failed');
    } finally {
      setActionLoading(null);
      setConfirmModal(null);
    }
  };

  const openConfirm = (action: string, container: string) => {
    setConfirmModal({ show: true, action, container });
  };

  return (
    <div className="min-h-screen bg-transparent p-4 md:p-8 space-y-8">
      <div className="flex items-center justify-between border-b border-slate-800 pb-6">
        <div>
          <h1 className="text-4xl font-black text-white tracking-tighter uppercase italic">Docker Management</h1>
          <p className="text-slate-400 font-medium mt-2">Monitor and control Docker containers</p>
          {stats?.warning && (
            <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-red-400" />
              <span className="text-xs font-bold text-red-400">{stats.warning}</span>
            </div>
          )}
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="h-11 px-6 bg-slate-900 border border-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-800 transition uppercase tracking-widest"
        >
          {loading ? 'Syncing...' : 'Refresh'}
        </button>
      </div>

      {loading && !stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="bg-slate-900/30 border border-slate-800 rounded-2xl p-6 animate-pulse">
              <div className="h-6 bg-slate-800 rounded w-1/3 mb-4" />
              <div className="space-y-3">
                <div className="h-20 bg-slate-800 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {stats.containers.map((container) => {
            const hasAlert = alerts[container.name] || container.needs_restart;
            return (
            <div key={container.container_id} className={`bg-slate-900/30 border rounded-2xl overflow-hidden ${hasAlert ? 'border-red-500' : 'border-slate-800'}`}>
              <div className="px-6 py-4 border-b border-slate-800/50 bg-slate-950/20 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center border ${hasAlert ? 'bg-red-500/10 border-red-500/20' : 'bg-purple-500/10 border-purple-500/20'}`}>
                    <Container size={16} className={hasAlert ? 'text-red-400' : 'text-purple-400'} />
                  </div>
                  <div>
                    <h3 className="text-xs font-black text-white uppercase">{container.name}</h3>
                    <span className="text-[8px] font-mono text-slate-600">#{container.container_id}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasAlert && (
                    <div className="flex items-center gap-1 bg-red-500/10 border border-red-500/20 px-2 py-1 rounded">
                      <AlertTriangle size={10} className="text-red-400" />
                      <span className="text-[8px] font-black text-red-400 uppercase">Needs Restart</span>
                    </div>
                  )}
                  <div className={`w-2 h-2 rounded-full ${hasAlert ? 'bg-red-500 animate-pulse' : 'bg-emerald-500 animate-pulse'}`} />
                </div>
              </div>

              <div className="p-6 space-y-4">
                {container.warning && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] font-black text-red-400 uppercase mb-1">Container Issue Detected</div>
                      <div className="text-[9px] text-red-300">{container.warning}</div>
                    </div>
                  </div>
                )}
                {hasAlert && !container.warning && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle size={16} className="text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="text-[10px] font-black text-red-400 uppercase mb-1">Container Issue Detected</div>
                      <div className="text-[9px] text-red-300">CPU at 0% for last 5 syncs. Container may need restart.</div>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 p-3 bg-slate-950/40 rounded-lg">
                    <Cpu size={14} className="text-blue-400" />
                    <div>
                      <div className="text-[8px] font-bold text-slate-600 uppercase">CPU</div>
                      <div className="text-sm font-black text-white">{container.cpu_percent}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-slate-950/40 rounded-lg">
                    <HardDrive size={14} className="text-pink-400" />
                    <div>
                      <div className="text-[8px] font-bold text-slate-600 uppercase">Memory</div>
                      <div className="text-sm font-black text-white">{container.memory_percent}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-slate-950/40 rounded-lg">
                    <Activity size={14} className="text-emerald-400" />
                    <div>
                      <div className="text-[8px] font-bold text-slate-600 uppercase">Net I/O</div>
                      <div className="text-[10px] font-black text-white">{container.net_io}</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 p-3 bg-slate-950/40 rounded-lg">
                    <Activity size={14} className="text-yellow-400" />
                    <div>
                      <div className="text-[8px] font-bold text-slate-600 uppercase">Block I/O</div>
                      <div className="text-[10px] font-black text-white">{container.block_io}</div>
                    </div>
                  </div>
                </div>

                <div className="text-[9px] text-slate-600 font-mono p-2 bg-slate-950/40 rounded">
                  Memory: {container.memory_usage} • PIDs: {container.pids}
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => openConfirm('restart', container.name)}
                    disabled={actionLoading === `restart-${container.name}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-black uppercase rounded-lg transition disabled:opacity-50"
                  >
                    <RefreshCw size={12} />
                    {actionLoading === `restart-${container.name}` ? 'Restarting...' : 'Restart'}
                  </button>
                  <button
                    onClick={() => openConfirm('stop', container.name)}
                    disabled={actionLoading === `stop-${container.name}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white text-[10px] font-black uppercase rounded-lg transition disabled:opacity-50"
                  >
                    <Square size={12} />
                    {actionLoading === `stop-${container.name}` ? 'Stopping...' : 'Stop'}
                  </button>
                  <button
                    onClick={() => openConfirm('start', container.name)}
                    disabled={actionLoading === `start-${container.name}`}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-black uppercase rounded-lg transition disabled:opacity-50"
                  >
                    <Play size={12} />
                    {actionLoading === `start-${container.name}` ? 'Starting...' : 'Start'}
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      ) : null}

      {confirmModal?.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setConfirmModal(null)} />
          <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center border border-yellow-500/20">
                <AlertTriangle size={24} className="text-yellow-400" />
              </div>
              <div>
                <h3 className="text-xl font-black text-white uppercase">Confirm Action</h3>
                <p className="text-sm text-slate-400 mt-1">This action will affect the container</p>
              </div>
            </div>
            <p className="text-slate-300 mb-6">
              Are you sure you want to <span className="font-black text-white uppercase">{confirmModal.action}</span> the container{' '}
              <span className="font-black text-blue-400">{confirmModal.container}</span>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleAction(confirmModal.action, confirmModal.container)}
                className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white text-xs font-black uppercase rounded-lg transition"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
